import axios from "axios";
import { guard } from "@/lib/unsavedGuard";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("ced_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (typeof navigator !== "undefined" && !navigator.onLine && isQueueable(config)) {
    config.adapter = async () => enqueue(config);
  }
  return config;
});

// ---- Mode hors connexion : file d'attente des modifications + cache de lecture ----
const QKEY = "ced_offline_queue";
const CKEY = "ced_offline_cache:";
const OFFLINE_OK = [/^\/appointments\/[^/]+(\/(photos|tech-sheet|checklist|restitution-meta))?$/, /^\/inspections(\/[^/]+)?$/, /^\/tasks(\/[^/]+)?$/, /^\/tools\/ptg(\/[^/]+)?$/, /^\/clients(\/[^/]+)?$/, /^\/vehicles(\/[^/]+)?$/];
const CACHE_OK = [/^\/appointments/, /^\/clients/, /^\/vehicles/, /^\/tasks/, /^\/services$/, /^\/tools\/products$/, /^\/me$/, /^\/auth\/me$/, /^\/business-profile$/, /^\/settings$/, /^\/team\/members$/];
const path = (c) => (c.url || "").replace(API, "").split("?")[0];
const isQueueable = (c) => ["post", "put", "patch", "delete"].includes((c.method || "").toLowerCase()) && !(c.data instanceof FormData) && OFFLINE_OK.some((r) => r.test(path(c)));
const readQ = () => { try { return JSON.parse(localStorage.getItem(QKEY) || "[]"); } catch { return []; } };
const writeQ = (q) => { localStorage.setItem(QKEY, JSON.stringify(q)); window.dispatchEvent(new CustomEvent("ced-offline-queue", { detail: q.length })); };
export const offlineQueue = () => readQ();

function enqueue(config) {
  let body = config.data;
  try { body = typeof body === "string" ? JSON.parse(body) : body; } catch { /* keep raw */ }
  writeQ([...readQ(), { id: Math.random().toString(36).slice(2), method: config.method, url: path(config), data: body ?? null, at: new Date().toISOString() }]);
  return Promise.resolve({ data: { ...(body && typeof body === "object" ? body : {}), ok: true, _queued: true }, status: 202, statusText: "Queued", headers: {}, config });
}

const cacheKey = (c) => CKEY + path(c) + "?" + new URLSearchParams(c.params || {}).toString();

api.interceptors.response.use((res) => {
  const c = res.config;
  if ((c.method || "get") !== "get" && !res._queued) guard.saved();
  if ((c.method || "get") === "get" && c.responseType !== "blob" && CACHE_OK.some((r) => r.test(path(c)))) {
    try { const s = JSON.stringify(res.data); if (s.length < 400000) localStorage.setItem(cacheKey(c), s); } catch { /* quota */ }
  }
  return res;
}, (err) => {
  const c = err.config;
  if (c && !err.response && !c._replay) {
    if ((c.method || "get") === "get") {
      const hit = localStorage.getItem(cacheKey(c));
      if (hit) return Promise.resolve({ data: JSON.parse(hit), status: 200, headers: {}, config: c, _offline: true });
    } else if (isQueueable(c)) return enqueue(c);
  }
  return Promise.reject(err);
});

let flushing = false;
export async function flushOfflineQueue() {
  if (flushing || !navigator.onLine) return { sent: 0, failed: 0 };
  flushing = true; let sent = 0; let failed = 0;
  try {
    for (const item of readQ()) {
      try {
        await api.request({ method: item.method, url: item.url, data: item.data, _replay: true });
        sent += 1; writeQ(readQ().filter((x) => x.id !== item.id));
      } catch (e) {
        if (!e.response) break;
        failed += 1; writeQ(readQ().filter((x) => x.id !== item.id));
      }
    }
  } finally { flushing = false; }
  window.dispatchEvent(new CustomEvent("ced-offline-synced", { detail: { sent, failed } }));
  return { sent, failed };
}
if (typeof window !== "undefined") window.addEventListener("online", () => setTimeout(flushOfflineQueue, 800));

export const fileSrc = (pathOrUrl) => {
  if (!pathOrUrl) return "";
  if (pathOrUrl.startsWith("http")) return pathOrUrl;
  if (pathOrUrl.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${pathOrUrl}`;
  return `${API}/files/${pathOrUrl}`;
};

export function formatApiError(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
// Fiche atelier quittée avec des modifications : sa sauvegarde est attendue avant de recharger le RDV.
let pending = Promise.resolve();
export const trackPendingSave = (p) => { pending = p.catch(() => {}); return p; };
export const waitPendingSave = () => pending;
